import { useState, useEffect } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Select, MenuItem,
  CircularProgress, Alert, IconButton, Typography, Button, TextField, Tooltip, Stack
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import KeyIcon from '@mui/icons-material/Key'
import BadgeIcon from '@mui/icons-material/Badge'
import EmailIcon from '@mui/icons-material/Email'
import { supabase } from '../supabase'

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY

// Mirrors MIN_PASSWORD_LENGTH in the read-api edge function, which is the
// authoritative check — this only gives immediate feedback in the form.
const MIN_PASSWORD_LENGTH = 8

interface User {
  id: string
  email: string
  username: string
  first_name: string
  last_name: string
  display_name: string
  role: string
  created_at: string
  last_sign_in_at: string | null
}

async function adminApi(action: string, params: Record<string, unknown> = {}) {
  const { data: { session } } = await supabase.auth.getSession()
  const res = await fetch(`${SUPABASE_URL}/functions/v1/read-api`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'x-user-token': session?.access_token || '',
    },
    body: JSON.stringify({ action, params }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(err.error || 'Request failed')
  }
  return res.json()
}

const UserManagementWidget = ({ open, onClose, authData }: { open: boolean; onClose: () => void; authData?: any }) => {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [updating, setUpdating] = useState<string | null>(null)

  // Add-user form
  const [addOpen, setAddOpen] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [newUsername, setNewUsername] = useState('')
  const [newFirstName, setNewFirstName] = useState('')
  const [newLastName, setNewLastName] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [newRole, setNewRole] = useState('viewer')
  const [saving, setSaving] = useState(false)

  // Set-password + delete dialogs, each holding the user they act on
  const [pwUser, setPwUser] = useState<User | null>(null)
  const [pwValue, setPwValue] = useState('')
  const [delUser, setDelUser] = useState<User | null>(null)

  // Edit-names dialog
  const [nameUser, setNameUser] = useState<User | null>(null)
  const [nmUsername, setNmUsername] = useState('')
  const [nmFirst, setNmFirst] = useState('')
  const [nmLast, setNmLast] = useState('')

  const currentUserId = authData?.user?.id

  const load = () => {
    setLoading(true)
    setError('')
    adminApi('list_users')
      .then((data: User[]) => setUsers(data.sort((a, b) => a.email.localeCompare(b.email))))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (!open) return
    setNotice('')
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleRoleChange = async (userId: string, newRoleValue: string) => {
    setUpdating(userId)
    setError(''); setNotice('')
    try {
      await adminApi('update_user_role', { user_id: userId, role: newRoleValue })
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRoleValue } : u))
    } catch (err: any) {
      setError(err.message)
    } finally {
      setUpdating(null)
    }
  }

  const handleAddUser = async () => {
    setSaving(true)
    setError(''); setNotice('')
    try {
      const res = await adminApi('create_user', {
        email: newEmail, password: newPassword, role: newRole, username: newUsername,
        first_name: newFirstName, last_name: newLastName,
      })
      setUsers(prev => [...prev, res.user].sort((a, b) => a.email.localeCompare(b.email)))
      setNotice(`Created ${res.user.email}. Give them the password directly — they are not emailed.`)
      setAddOpen(false)
      setNewEmail(''); setNewUsername(''); setNewPassword(''); setNewRole('viewer')
      setNewFirstName(''); setNewLastName('')
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleSetPassword = async () => {
    if (!pwUser) return
    setSaving(true)
    setError(''); setNotice('')
    try {
      await adminApi('set_user_password', { user_id: pwUser.id, password: pwValue })
      setNotice(`Password changed for ${pwUser.email}. They are not notified — tell them directly.`)
      setPwUser(null); setPwValue('')
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!delUser) return
    setSaving(true)
    setError(''); setNotice('')
    try {
      await adminApi('delete_user', { user_id: delUser.id })
      setUsers(prev => prev.filter(u => u.id !== delUser.id))
      setNotice(`Deleted ${delUser.email}.`)
      setDelUser(null)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const openNameEditor = (user: User) => {
    setNameUser(user)
    setNmUsername(user.username === user.email ? '' : user.username)
    setNmFirst(user.first_name || '')
    setNmLast(user.last_name || '')
    setError(''); setNotice('')
  }

  const handleSaveNames = async () => {
    if (!nameUser) return
    setSaving(true)
    setError(''); setNotice('')
    try {
      const res = await adminApi('update_user_names', {
        user_id: nameUser.id,
        username: nmUsername,
        first_name: nmFirst,
        last_name: nmLast,
      })
      setUsers(prev => prev.map(u => u.id === nameUser.id ? {
        ...u,
        username: res.username,
        first_name: res.first_name,
        last_name: res.last_name,
        display_name: res.display_name,
      } : u))
      setNotice(`Updated names for ${nameUser.email}.`)
      setNameUser(null)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleSendReset = async (user: User) => {
    setUpdating(user.id)
    setError(''); setNotice('')
    try {
      await adminApi('send_password_reset', { email: user.email })
      setNotice(`Password-reset email sent to ${user.email}.`)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setUpdating(null)
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          User Management
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

          <Button
            startIcon={<PersonAddAlt1Icon />}
            variant="contained"
            size="small"
            sx={{ mb: 2 }}
            onClick={() => { setAddOpen(true); setError(''); setNotice('') }}
          >
            Add user
          </Button>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><CircularProgress /></div>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell><strong>Username</strong></TableCell>
                    <Tooltip title="Shown on the map and in the item modal as the geotagger's name" placement="top">
                      <TableCell><strong>Displayed name</strong></TableCell>
                    </Tooltip>
                    <TableCell><strong>Role</strong></TableCell>
                    <TableCell><strong>Last Sign In</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map(user => {
                    const isSelf = user.id === currentUserId
                    const isAdmin = user.role === 'admin'
                    return (
                      <TableRow key={user.id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.85em' }}>
                            {user.email}{isSelf && <em style={{ color: '#888' }}> (you)</em>}
                          </Typography>
                        </TableCell>
                        <TableCell>{user.username}</TableCell>
                        <TableCell>
                          {user.display_name
                            ? <Typography variant="body2" sx={{ fontSize: '0.85em' }}>{user.display_name}</Typography>
                            // A blank display name means anything this user geotags
                            // is credited to nobody on the map.
                            : <Tooltip title="Not set — their geotags will show no name on the map">
                                <Typography variant="body2" sx={{ fontSize: '0.85em', color: '#c62828' }}>not set</Typography>
                              </Tooltip>}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={user.role}
                            onChange={(e) => handleRoleChange(user.id, e.target.value)}
                            size="small"
                            // Changing your own role would drop your admin access
                            // mid-session; the server refuses it too.
                            disabled={updating === user.id || isSelf}
                            sx={{ minWidth: 100, fontSize: '0.85em' }}
                          >
                            <MenuItem value="admin">Admin</MenuItem>
                            <MenuItem value="editor">Editor</MenuItem>
                            <MenuItem value="viewer">Viewer</MenuItem>
                          </Select>
                          {updating === user.id && <CircularProgress size={16} sx={{ ml: 1 }} />}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.8em', color: '#666' }}>
                            {user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString() : 'Never'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit username and displayed name">
                            <span>
                              <IconButton size="small" disabled={updating === user.id}
                                onClick={() => openNameEditor(user)}>
                                <BadgeIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Set a new password directly">
                            <span>
                              <IconButton size="small" disabled={updating === user.id}
                                onClick={() => { setPwUser(user); setPwValue(''); setError(''); setNotice('') }}>
                                <KeyIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Email them a password-reset link">
                            <span>
                              <IconButton size="small" disabled={updating === user.id}
                                onClick={() => handleSendReset(user)}>
                                <EmailIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={
                            isSelf ? "You cannot delete your own account"
                              : isAdmin ? "Change the role to Editor or Viewer first"
                              : "Delete this user"
                          }>
                            <span>
                              <IconButton size="small" color="error"
                                disabled={updating === user.id || isSelf || isAdmin}
                                onClick={() => { setDelUser(user); setError(''); setNotice('') }}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      {/* Add user */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add user</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Email" type="email" size="small" fullWidth autoFocus
              value={newEmail} onChange={e => setNewEmail(e.target.value)} />
            <TextField label="Username (optional)" size="small" fullWidth
              value={newUsername} onChange={e => setNewUsername(e.target.value)}
              helperText="Shown on their comments. Defaults to the email address." />
            <Stack direction="row" spacing={1}>
              <TextField label="First name" size="small" fullWidth
                value={newFirstName} onChange={e => setNewFirstName(e.target.value)} />
              <TextField label="Last name" size="small" fullWidth
                value={newLastName} onChange={e => setNewLastName(e.target.value)} />
            </Stack>
            <Typography variant="caption" sx={{ color: '#666', mt: -1 }}>
              First and last name are what the map credits for their geotags. Leave
              them blank and their geotags show no name.
            </Typography>
            <TextField label="Password" type="text" size="small" fullWidth
              value={newPassword} onChange={e => setNewPassword(e.target.value)}
              error={newPassword.length > 0 && newPassword.length < MIN_PASSWORD_LENGTH}
              helperText={`At least ${MIN_PASSWORD_LENGTH} characters. Shown in clear text so you can pass it on — the user is not emailed.`} />
            <Select size="small" value={newRole} onChange={e => setNewRole(e.target.value)}>
              <MenuItem value="viewer">Viewer — read only</MenuItem>
              <MenuItem value="editor">Editor — can geotag and comment</MenuItem>
              <MenuItem value="admin">Admin — full access</MenuItem>
            </Select>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleAddUser}
            disabled={saving || !newEmail.includes('@') || newPassword.length < MIN_PASSWORD_LENGTH}>
            {saving ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit names */}
      <Dialog open={!!nameUser} onClose={() => setNameUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit names</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>{nameUser?.email}</Typography>
          <Stack spacing={2}>
            <TextField label="Username" size="small" fullWidth autoFocus
              value={nmUsername} onChange={e => setNmUsername(e.target.value)}
              helperText="Shown on their comments. Blank falls back to the email address." />
            <Stack direction="row" spacing={1}>
              <TextField label="First name" size="small" fullWidth
                value={nmFirst} onChange={e => setNmFirst(e.target.value)} />
              <TextField label="Last name" size="small" fullWidth
                value={nmLast} onChange={e => setNmLast(e.target.value)} />
            </Stack>
            <Typography variant="caption" sx={{ color: '#666' }}>
              Displayed name on the map:{' '}
              <strong>
                {[nmFirst, nmLast].filter(Boolean).join(' ').trim() || nmUsername || '(none — geotags will show no name)'}
              </strong>
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameUser(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveNames} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Set password */}
      <Dialog open={!!pwUser} onClose={() => setPwUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Set password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>{pwUser?.email}</Typography>
          <TextField label="New password" type="text" size="small" fullWidth autoFocus
            value={pwValue} onChange={e => setPwValue(e.target.value)}
            error={pwValue.length > 0 && pwValue.length < MIN_PASSWORD_LENGTH}
            helperText={`At least ${MIN_PASSWORD_LENGTH} characters. The user is not notified — tell them directly.`} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwUser(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSetPassword}
            disabled={saving || pwValue.length < MIN_PASSWORD_LENGTH}>
            {saving ? <CircularProgress size={20} /> : 'Set password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!delUser} onClose={() => setDelUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete user?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{delUser?.email}</strong> will be permanently removed and will no longer be able to sign in.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
            Photographs they geotagged stay on the map, but their name will no longer appear against them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDelUser(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default UserManagementWidget
